/**
 * ApiClient - 业务 HTTP 客户端（fetch + 401 直接踢人）
 *
 * 设计要点：
 * - 不再做主动刷新（后端自动续期）
 * - 任何 401（HTTP 或 body.code）→ 立即清 token + 触发 onUnauthorized + 抛错
 * - Authorization header 只附加给 admin.wwls.net 域名，避免泄露给其他第三方
 * - 提供 callProfile() 裸 fetch 方法供 AuthService 心跳调用（不进 request 拦截链）
 */

import tokenStore from './TokenStore';

export const API_BASE = 'https://admin.wwls.net/api';
const API_HOST = 'admin.wwls.net';

export interface ApiRequestInit extends RequestInit {
  /** 业务 JSON body，自动序列化 + 设置 Content-Type */
  json?: unknown;
  /** multipart/form-data 表单。设置后 Content-Type 由浏览器自动加 boundary，不要手动 set */
  formData?: FormData;
}

/**
 * profile 校验结果（tagged union）
 * - ok=true: token 仍然有效
 * - reason='unauthorized': 后端明确拒绝（4xx / body.code 非 0 / 无 data）
 * - reason='transient': 网络问题 / 5xx / 解析失败，不能断言 token 失效
 */
export type ProfileOutcome =
  | { ok: true; user: unknown }
  | { ok: false; reason: 'unauthorized' | 'transient' };

interface PassportResponse<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
}

class ApiClient {
  private onUnauthorizedCb: (() => void) | null = null;

  /**
   * 通用业务请求。响应按 JSON 解析。
   * 401（HTTP 或 body.code）→ 直接清 token + 抛错（不再做刷新）。
   * 非 2xx 抛错，调用方按需 try/catch。
   */
  async request<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
    const url = this._buildUrl(path);
    const { json, formData, headers: rawHeaders, body: rawBody, ...rest } = init;

    const headers = new Headers(rawHeaders || {});
    let body: BodyInit | null | undefined = rawBody as BodyInit | null | undefined;
    if (formData !== undefined) {
      // 不主动设 Content-Type，让浏览器自动添加 multipart boundary
      body = formData;
    } else if (json !== undefined) {
      body = JSON.stringify(json);
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    }

    // 仅 admin.wwls.net 域名加 token，避免泄露给其他第三方
    if (this._shouldAttachToken(url)) {
      const token = tokenStore.getToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    const resp = await fetch(url, { ...rest, headers, body });

    // 解析响应体（尽量按 JSON）
    const ct = resp.headers.get('content-type') || '';
    let parsed: unknown = null;
    if (ct.includes('application/json')) {
      try { parsed = await resp.json(); } catch { parsed = null; }
    } else {
      const text = await resp.text();
      if (text) {
        try { parsed = JSON.parse(text); } catch { parsed = text; }
      }
    }

    // 401 判断（HTTP 或 业务 code）
    const bodyCode = (parsed && typeof parsed === 'object' && 'code' in parsed)
      ? (parsed as PassportResponse).code
      : undefined;
    const isUnauthorized = resp.status === 401 || bodyCode === 401;

    if (isUnauthorized) {
      // 不再尝试刷新，直接踢人
      tokenStore.clearAuth();
      this.onUnauthorizedCb?.();
      throw new Error('unauthorized');
    }

    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}: ${typeof parsed === 'string' ? parsed : JSON.stringify(parsed)}`);
    }

    return parsed as T;
  }

  /**
   * 调 /passport/profile 校验 token（裸 fetch，不进 request 拦截链）。
   * AuthService 的首次校验和心跳都用这个，避免触发 onUnauthorized 双重链路。
   */
  async callProfile(token: string): Promise<ProfileOutcome> {
    if (!token) return { ok: false, reason: 'unauthorized' };

    let resp: Response;
    try {
      resp = await fetch(this._buildUrl('/passport/profile'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
    } catch {
      return { ok: false, reason: 'transient' }; // 网络错误
    }

    if (resp.status >= 500) {
      return { ok: false, reason: 'transient' };
    }

    let body: PassportResponse | null = null;
    try {
      body = await resp.json();
    } catch {
      return { ok: false, reason: resp.status >= 400 && resp.status < 500 ? 'unauthorized' : 'transient' };
    }

    if (resp.status >= 400 && resp.status < 500) {
      return { ok: false, reason: 'unauthorized' };
    }
    if (body?.code !== undefined && body.code !== 0) {
      return { ok: false, reason: 'unauthorized' };
    }

    // 兼容两种返回结构：{code, data: user} 和 直接 user 对象
    const user = body?.data !== undefined ? body.data : body;
    if (!user) {
      return { ok: false, reason: 'unauthorized' };
    }

    return { ok: true, user };
  }

  /** 注册 401 兜底失败回调（AuthService 调用） */
  onUnauthorized(cb: () => void): void {
    this.onUnauthorizedCb = cb;
  }

  private _buildUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const base = API_BASE.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : '/' + path;
    return base + p;
  }

  /** 仅 admin.wwls.net 域名才附加 token */
  private _shouldAttachToken(url: string): boolean {
    try {
      const u = new URL(url);
      return u.host === API_HOST;
    } catch {
      return false;
    }
  }
}

export const apiClient = new ApiClient();
export default apiClient;
